<script setup lang="ts">
/**
 * The rank ladder: all twenty rungs, what each one costs and what each one
 * hands over.
 *
 * ── Where the list comes from ────────────────────────────────────────────────
 * The server, entirely. Titles, thresholds, unlock names and which rungs the
 * player has reached all arrive on the garage overview, so this file holds **no
 * copy of the rank list** — the same rule the research tree follows for its
 * branches. A twenty-first rank is a title in `catalogue.data.ts` and a file in
 * `public/assets/ranks/`; nothing here would need touching.
 *
 * ── Why the insignia is not on the wire ──────────────────────────────────────
 * `rankArtPath` derives the path from the rank number, zero-padded to two
 * digits, in the same spirit as a map's picture (`/assets/maps/<id>.webp`) and a
 * hull part's. A path is not a fact worth sending, and a served path is a second
 * place for the naming convention to live. A missing file falls back to a plain
 * plate carrying the number — never a broken image, which is the same working
 * state `model: null` gives a prop.
 *
 * ── It runs left to right, and that is a layout rule rather than a taste ─────
 * A ladder is a *progression*, and a progression that runs down a scrolling
 * column reads as a list of records. Horizontally the shape is the subject: the
 * reached rungs are behind you on the left, the rail brightens up to where you
 * stand, and everything ahead is dimmed to the right. It replaced a vertical
 * list of twenty rows.
 *
 * **It is a native horizontal scroller, deliberately not the research tree's
 * pan-and-zoom.** That machinery (`pan-zoom.ts`) exists for a 2-D graph that
 * does not fit either axis, and it costs a transform, a wheel listener that must
 * `preventDefault`, clamped pan bounds and a scroll container it then has to
 * fight (a hidden `overflow` box still scrolls itself to reveal a focused card,
 * which is exactly the bug that file documents). This is a 1-D strip: a real
 * scroll container gives wheel, trackpad, touch, keyboard and `scrollIntoView`
 * for free, and a browser scrolling a focused rung into view is *wanted* here
 * rather than a defect to work around.
 *
 * ── The one measured layout constraint ───────────────────────────────────────
 * **The insignia is drawn at 96 px and must never be shrunk below 64.** At 64
 * the chevrons are countable and at 32 they are not, which makes a Sergeant and
 * a Staff Sergeant the same picture — the whole reason this window exists rather
 * than the artwork being squeezed into the title-bar chip, which stays textual.
 * 96 px is the horizontal layout's dividend: a card is as wide as its own
 * content now rather than sharing a row with three text columns, so the art gets
 * a third more size for no extra window width.
 *
 * The art is transparent and drawn for the dark garage chrome, so the cards are
 * dark. Do not put these on a light plate.
 */
import { computed, nextTick, onMounted, ref } from 'vue';
import type { RankRung, Wallet } from '@/net/api';
import { formatCredits } from './stat-display';
import { rankArtPath, rankProgress } from './rank-ladder';

const props = defineProps<{
  ranks: RankRung[];
  /** For the player's own rank and their progress into the next one. */
  wallet: Wallet | null;
}>();

const emit = defineEmits<{ close: [] }>();

/**
 * Ranks whose art failed to load, so the fallback plate is drawn instead.
 *
 * Replaced rather than mutated: a `Set` mutated in place is not reactive. Same
 * shape as `BattlePicker`'s missing-map-art set, for the same reason.
 */
const missingArt = ref(new Set<number>());
function noteMissing(rank: number): void {
  missingArt.value = new Set(missingArt.value).add(rank);
}
function artFor(rank: number): string | null {
  if (missingArt.value.has(rank)) return null;
  return rankArtPath(rank);
}

const currentRank = computed(() => props.wallet?.rank ?? 0);

/** 0–1 through the current rank, or null with no wallet. See `rankProgress`. */
const progress = computed(() => rankProgress(props.wallet));

const dialog = ref<HTMLElement | null>(null);

/**
 * The player's own card, kept as a plain variable rather than a ref.
 *
 * Nothing renders from it — it is scrolled to once and never read again — and a
 * reactive element handle would invalidate the list every time the ladder
 * re-rendered for no gain.
 */
let currentCard: HTMLElement | null = null;
function markCurrent(el: unknown, rank: number): void {
  if (rank === currentRank.value && el) currentCard = el as HTMLElement;
}

/**
 * Focus lands in the dialog, and the player's own rung is scrolled to.
 *
 * Focus first so Escape and Tab go somewhere sensible rather than into the
 * garage behind the scrim — `BattlePicker`'s convention. The scroll is the
 * ladder's own: twenty rungs do not fit any laptop at this card size, and
 * opening at rank 1 would put a Colonel's own standing three screens off to the
 * right, when *where am I* is the question the window is opened to answer.
 *
 * `inline: 'center'` — the axis changed with the layout, and `block` here would
 * silently do nothing on a horizontal scroller. `behavior: 'instant'`, because
 * the window is appearing at the same moment: a smooth scroll would animate from
 * a position the player never saw, which reads as the ladder sliding away from
 * them.
 */
onMounted(async () => {
  dialog.value?.focus();
  await nextTick();
  currentCard?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'instant' });
});
</script>

<template>
  <div class="scrim" data-test="rank-ladder" @click.self="emit('close')">
    <section
      ref="dialog"
      class="window"
      role="dialog"
      aria-modal="true"
      aria-label="Rank ladder"
      tabindex="-1"
      @keydown.esc="emit('close')"
    >
      <header>
        <div class="heading">
          <h2>Ranks</h2>
          <p class="standing" data-test="rank-standing">
            <template v-if="wallet">
              <strong>{{ wallet.rankTitle }}</strong>
              <span class="muted">rank {{ wallet.rank }} of {{ ranks.length }}</span>
            </template>
            <template v-else>Reading your service record…</template>
          </p>
        </div>

        <button type="button" class="close" data-test="rank-ladder-close" @click="emit('close')">
          <span class="close-x" aria-hidden="true">×</span>
          <span class="close-word">Close</span>
        </button>
      </header>

      <div v-if="wallet && progress !== null" class="progress" data-test="rank-progress">
        <div class="track"><i :style="{ width: `${progress * 100}%` }"></i></div>
        <span v-if="wallet.xpForNextRank !== null" class="figures">
          {{ formatCredits(wallet.xpIntoRank) }} /
          {{ formatCredits(wallet.xpForNextRank) }} xp to the next rank
        </span>
        <span v-else class="figures">Top of the ladder — {{ formatCredits(wallet.xp) }} xp</span>
      </div>

      <ol
        class="ladder"
        data-test="rank-strip"
        tabindex="0"
        role="list"
        aria-label="Every rank, earliest first"
      >
        <li
          v-for="rung in ranks"
          :key="rung.rank"
          :ref="(el) => markCurrent(el, rung.rank)"
          class="rung"
          :class="{ reached: rung.reached, current: rung.rank === currentRank }"
          :data-test="`rank-${rung.rank}`"
          :aria-current="rung.rank === currentRank ? 'true' : undefined"
        >
          <span class="number">{{ rung.rank }}</span>

          <span class="insignia">
            <img
              v-if="artFor(rung.rank)"
              :src="artFor(rung.rank)!"
              :alt="`${rung.title} insignia`"
              :data-test="`rank-art-${rung.rank}`"
              decoding="async"
              @error="noteMissing(rung.rank)"
            />
            <span v-else class="art-placeholder" aria-hidden="true">{{ rung.rank }}</span>
          </span>

          <span class="detail">
            <strong class="name" :data-test="`rank-title-${rung.rank}`">{{ rung.title }}</strong>
            <span v-if="rung.rank === currentRank" class="tag" data-test="rank-current">
              You are here
            </span>
            <span class="cost">
              <template v-if="rung.xpToReach > 0">
                {{ formatCredits(rung.xpToReach) }} xp
              </template>
              <template v-else>From the first day</template>
            </span>
            <span v-if="rung.unlocks.length" class="unlocks" :data-test="`rank-unlocks-${rung.rank}`">
              Unlocks {{ rung.unlocks.join(' · ') }}
            </span>
            <span
              v-if="rung.awards.length"
              class="unlocks awarded"
              :data-test="`rank-awards-${rung.rank}`"
            >
              Awards {{ rung.awards.join(' · ') }}
            </span>
            <span
              v-else-if="!rung.unlocks.length"
              class="unlocks none"
            >Honours only</span>
          </span>
        </li>
      </ol>
    </section>
  </div>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  place-items: center;
  background: rgba(6, 8, 11, 0.72);
  z-index: 40;
}

/*
 * Wide, because the ladder is now horizontal: the window is a viewport onto a
 * strip rather than a column of rows. Capped at 96vw so it never touches the
 * viewport edge, and `max-height` is content-driven — the strip scrolls
 * sideways, so this window is short.
 *
 * `dvh`, not `vh`: on iOS Safari 15+, `vh` is sized against the *largest*
 * viewport (address bar hidden), so `88vh` is taller than what is actually on
 * screen whenever the bar is showing — which on a page that never scrolls
 * (`html, body, #app { overflow: hidden }`, see `style.css`) is always. The
 * window was genuinely bigger than the visible window. `dvh` tracks the real,
 * current viewport instead.
 */
.window {
  width: min(76rem, 96vw);
  max-height: 88dvh;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 0.5rem;
  background: #12151a;
  overflow: hidden;
}

header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1.2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

h2 {
  margin: 0;
  font-size: 1.05rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.standing {
  margin: 0.15rem 0 0;
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #e8722c;
}

.standing .muted {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.7rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

/* 44×44 minimum, per the project's own input rules. `flex: none` so a long rank
   title in the heading cannot squeeze the one control this window has. */
.close {
  flex: none;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 44px;
  min-height: 44px;
  padding: 0 1rem;
  border: 1px solid var(--steel-600, #3a424c);
  border-radius: 0.25rem;
  background: transparent;
  color: var(--steel-200, #d7dde3);
  font-weight: 600;
  font-size: 0.82rem;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: border-color 120ms ease, color 120ms ease, background-color 120ms ease;
}

.close:hover,
.close:focus-visible {
  border-color: var(--accent, #e8722c);
  color: var(--accent, #e8722c);
  background: rgba(232, 114, 44, 0.08);
}

.close-x {
  font-size: 1.15rem;
  line-height: 1;
  /* Optical: the glyph sits high in its box. */
  transform: translateY(-1px);
}

.progress {
  padding: 0.75rem 1.2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.track {
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.track i {
  display: block;
  height: 100%;
  background: #e8722c;
}

.figures {
  display: block;
  margin-top: 0.35rem;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}

/*
 * The strip: one row, scrolled sideways.
 *
 * `scroll-padding-inline` keeps `scrollIntoView` from parking a card flush
 * against the window edge, and proximity snapping settles a flick on a card
 * rather than mid-way between two — `mandatory` is deliberately avoided, since it
 * fights `inline: 'center'` on open.
 */
.ladder {
  margin: 0;
  padding: 1.1rem 1.2rem 1.3rem;
  list-style: none;
  display: flex;
  align-items: stretch;
  gap: 0.55rem;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x proximity;
  scroll-padding-inline: 1.2rem;
  overscroll-behavior-x: contain;
}

.ladder:focus-visible {
  outline: 1px solid rgba(232, 114, 44, 0.5);
  outline-offset: -2px;
}

/*
 * A card, not a row. Fixed width so twenty of them are a rhythm rather than
 * twenty different shapes — the widest content (an unlock list) would otherwise
 * make one card three times its neighbour.
 */
.rung {
  position: relative;
  flex: none;
  width: 11.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  padding: 0.9rem 0.7rem 1rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 0.4rem;
  /* Transparent art on dark chrome: no plate, by design. */
  background: rgba(255, 255, 255, 0.02);
  scroll-snap-align: center;
  text-align: center;
}

/*
 * The rail between cards, which is what makes the row read as a ladder rather
 * than as a shelf. Drawn per card as its own left-hand segment — accent while
 * that rung is reached, steel while it is not — so the "how far along am I"
 * gradient falls out of the server's `reached` flags with no global arithmetic
 * and no second source of truth. The first card has no segment to its left.
 */
.rung + .rung::before {
  content: '';
  position: absolute;
  left: -0.6rem;
  top: 3.1rem;
  width: 0.65rem;
  height: 2px;
  background: rgba(255, 255, 255, 0.12);
}

.rung.reached + .rung.reached::before,
.rung.reached + .rung.current::before {
  background: rgba(232, 114, 44, 0.55);
}

/* An unreached rung is dimmed rather than hidden — the whole point of the
   window is to show what is ahead as well as what is behind. */
.rung:not(.reached) {
  opacity: 0.45;
}

/*
 * The current rank, which is the subject of the window.
 *
 * Four channels, three of which need no motion at all: the accent border, the
 * lifted background, the "You are here" tag and the brighter number. The
 * animation is the fourth, and it is a `box-shadow`/`opacity` pulse on a
 * pseudo-element rather than anything per-frame in JS — this window sits over a
 * live WebGL hangar scene, so the effect has to be something the compositor can
 * do on its own.
 */
.rung.current {
  border-color: rgba(232, 114, 44, 0.75);
  background: rgba(232, 114, 44, 0.1);
  box-shadow: 0 0 0 1px rgba(232, 114, 44, 0.25), 0 6px 22px rgba(232, 114, 44, 0.14);
}

.rung.current::after {
  content: '';
  position: absolute;
  inset: -3px;
  border: 1px solid rgba(232, 114, 44, 0.85);
  border-radius: 0.5rem;
  pointer-events: none;
  animation: rank-pulse 2.4s ease-in-out infinite;
}

@keyframes rank-pulse {
  0%,
  100% {
    opacity: 0.9;
    transform: scale(1);
  }
  50% {
    opacity: 0.25;
    transform: scale(1.012);
  }
}

/*
 * Reduced motion keeps the mark and drops the movement.
 *
 * The ring stays, at a steady mid opacity — the project's rule is that reduced
 * motion disables non-essential *animation*, not that it removes information, and
 * "which rank am I" is the one thing this window exists to say.
 */
@media (prefers-reduced-motion: reduce) {
  .rung.current::after {
    animation: none;
    opacity: 0.7;
  }
}

.number {
  font-variant-numeric: tabular-nums;
  font-size: 0.72rem;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.35);
}

.rung.current .number {
  color: rgba(255, 255, 255, 0.75);
}

.insignia {
  width: 96px;
  height: 96px;
  display: grid;
  place-items: center;
}

/* 96 px of drawn insignia. Below about 64 the chevrons stop being countable —
   measured, not preferred. See the note at the top of this file. */
.insignia img {
  width: 96px;
  height: 96px;
  object-fit: contain;
}

.art-placeholder {
  width: 96px;
  height: 96px;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.3rem;
  background: repeating-linear-gradient(
    -45deg,
    #23282f,
    #23282f 6px,
    #1c2027 6px,
    #1c2027 12px
  );
  color: rgba(255, 255, 255, 0.42);
  font-size: 1.1rem;
  font-variant-numeric: tabular-nums;
}

.detail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  min-width: 0;
  width: 100%;
}

.name {
  font-size: 0.86rem;
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.92);
}

.tag {
  padding: 0.1rem 0.4rem;
  border: 1px solid rgba(232, 114, 44, 0.6);
  border-radius: 2px;
  color: #e8722c;
  font-size: 0.58rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
}

.cost {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}

.unlocks {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.72rem;
  line-height: 1.3;
  /* Wrapping rather than truncating: a rung that gates four items is exactly the
     rung somebody opened this window to read. */
  overflow-wrap: anywhere;
}

.unlocks.none {
  color: rgba(255, 255, 255, 0.25);
}

/* The one line on this ladder that is a *reward* rather than a price list, in the
   diamond blue the shop uses for its best shelf. It is the whole reason the top
   three rungs are worth reading now. */
.unlocks.awarded {
  color: #9fd7f5;
}

/*
 * Narrow viewports get smaller cards, never smaller insignia than the 64 px
 * floor the art was measured at. The card gives up width and the pictures do not
 * give up legibility — which is the same trade the old vertical layout made.
 */
@media (max-width: 60rem) {
  .rung {
    width: 10rem;
    padding: 0.8rem 0.55rem 0.9rem;
  }

  .insignia,
  .insignia img,
  .art-placeholder {
    width: 80px;
    height: 80px;
  }

  .rung + .rung::before {
    top: 2.7rem;
  }
}

/*
 * A phone in landscape: short, not merely narrow — the `≤60rem` step above
 * already fires here on width alone, so this is keyed on height, the same
 * `(max-height: 520px)` a phone-landscape pass uses elsewhere in this window
 * system (`SettingsPanel.vue`, `GarageScreen.vue`'s `.compact`).
 *
 * Everything here is the whole window at roughly **1.5x smaller** than the
 * base card — the insignia is exactly that, 96px → 64px, which is also
 * `.insignia`'s own documented floor (see the file header): the chevrons stop
 * being countable below it, so this is as far as the picture can shrink, not
 * a number chosen to hit a ratio. The rail's `top` offset is not measured at
 * this size — no browser was available this session — so it continues the
 * existing 96px→3.1rem / 80px→2.7rem line's own slope (−0.025rem per px of
 * insignia) rather than a fresh guess: 2.7 − 16×0.025 = 2.3rem.
 */
@media (max-height: 520px) {
  header {
    padding: 0.6rem 0.8rem;
  }

  .progress {
    padding: 0.5rem 0.8rem;
  }

  .ladder {
    padding: 0.75rem 0.8rem 0.85rem;
    gap: 0.35rem;
  }

  .rung {
    width: 7.5rem;
    padding: 0.6rem 0.45rem 0.65rem;
    gap: 0.2rem;
  }

  .insignia,
  .insignia img,
  .art-placeholder {
    width: 64px;
    height: 64px;
  }

  .rung + .rung::before {
    top: 2.3rem;
  }
}
</style>
